import { useSearchStore } from "@/stores/useSearchStore";
import { useChatStore } from "@/stores/useChatStore";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { openQuickOpen } from "./quick-open-event";
import { saveAllDirtyFiles } from "./save-all";
import { parseCombo, type ShortcutDef } from "./shortcuts";

export const FOCUS_CHAT_SEARCH_EVENT = "izanagi:focus-chat-search";
export const CLOSE_ACTIVE_FILE_EVENT = "izanagi:close-active-file";
export const TOGGLE_SHORTCUT_HELP_EVENT = "izanagi:toggle-shortcut-help";
/** ワークスペース検索入力欄へフォーカスを移すイベント。 */
export const FOCUS_WORKSPACE_SEARCH_EVENT = "izanagi:focus-workspace-search";

function isChatView(): boolean {
	return useLayoutStore.getState().mainView === "chats";
}

function dispatch(eventName: string): () => void {
	return () => window.dispatchEvent(new CustomEvent(eventName));
}

/** 左ペインの中にある UI を使うショートカット用。閉じたままだと操作対象が見えない。 */
function revealLeftPane(): void {
	useLayoutStore.getState().setLeftPaneCollapsed(false);
}

export const SHORTCUT_DEFS: readonly ShortcutDef[] = [
	// Windows では Ctrl+W がブラウザ予約のためページまで届かない場合がある。
	{
		id: "close-active-file",
		combo: parseCombo("ctrl+w"),
		description: "アクティブなファイルタブを閉じる",
		enabled: isChatView,
		run: dispatch(CLOSE_ACTIVE_FILE_EVENT),
	},
	{
		id: "toggle-sidebar",
		combo: parseCombo("mod+b"),
		description: "サイドバーを開閉する",
		allowWhileEditing: true,
		run: () => useLayoutStore.getState().toggleSidebar(),
	},
	{
		id: "toggle-terminal",
		combo: parseCombo("mod+j"),
		description: "ターミナルを開閉する",
		allowWhileEditing: true,
		enabled: isChatView,
		run: () => useLayoutStore.getState().toggleBottomPaneTab("terminal"),
	},
	{
		id: "focus-chat-search",
		combo: parseCombo("mod+k"),
		description: "チャットを検索する",
		allowWhileEditing: true,
		enabled: isChatView,
		run: () => {
			revealLeftPane();
			window.dispatchEvent(new CustomEvent(FOCUS_CHAT_SEARCH_EVENT));
		},
	},
	{
		id: "focus-workspace-search",
		combo: parseCombo("mod+shift+f"),
		description: "ワークスペースを検索する",
		allowWhileEditing: true,
		enabled: () => isChatView() && useChatStore.getState().environmentFileChildren !== null,
		run: () => {
			revealLeftPane();
			useSearchStore.getState().openSearch();
			requestAnimationFrame(dispatch(FOCUS_WORKSPACE_SEARCH_EVENT));
		},
	},
	{
		id: "quick-open",
		combo: parseCombo("mod+p"),
		description: "ファイルをクイックオープンする",
		allowWhileEditing: true,
		allowInMonaco: true,
		alwaysPreventDefault: true,
		enabled: () => isChatView() && useChatStore.getState().environmentFileChildren !== null,
		run: () => openQuickOpen(),
	},
	{
		id: "toggle-shortcut-help",
		combo: parseCombo("mod+/"),
		description: "キーボードショートカットを表示する",
		allowWhileEditing: true,
		run: dispatch(TOGGLE_SHORTCUT_HELP_EVENT),
	},
	// コンポーザー (ComposerInput) 側で処理する定義。run を持たせず、一覧への掲載だけを担う。
	{
		id: "search-transcript",
		combo: parseCombo("ctrl+f"),
		description: "チャット履歴を検索する（コンポーザー内）",
	},
	// ここから下は Monaco 側で処理する定義。run を持たせず、ショートカット一覧への掲載だけを担う。
	// useGlobalShortcuts は mod/ctrl 修飾のないイベントを早期 return するため、run を伴う
	// ショートカットを mod/ctrl なしで足してはならない (エディタ内で処理すること)。
	{
		id: "save-file",
		combo: parseCombo("mod+s"),
		description: "ファイルを保存する（エディタ内）",
	},
	{
		id: "save-all-files",
		combo: parseCombo("mod+shift+s"),
		description: "変更のあるファイルをすべて保存する",
		allowWhileEditing: true,
		allowInMonaco: true,
		alwaysPreventDefault: true,
		enabled: () => isChatView() && useChatStore.getState().dirtyFileIds.length > 0,
		run: () => void saveAllDirtyFiles(),
	},
	{
		id: "quick-outline",
		combo: parseCombo("mod+shift+o"),
		description: "シンボルへ移動する（エディタ内）",
	},
	{
		id: "toggle-word-wrap",
		combo: parseCombo("alt+z"),
		description: "行の折り返しを切り替える（エディタ内）",
	},
	{
		id: "go-to-definition",
		combo: { key: "f12", mod: false, ctrl: false, shift: false, alt: false },
		description: "定義へ移動する（エディタ内）",
	},
	{
		id: "find-references",
		combo: parseCombo("shift+f12"),
		description: "参照を検索する（エディタ内）",
	},
];

if (import.meta.env.DEV) {
	// useGlobalShortcuts は mod/ctrl 修飾のないイベントを早期 return するため、run を持つ
	// ショートカットに mod/ctrl がないと静かに一度も実行されない。コメントだけでは再発を防げないため、
	// 起動時に検出する。
	for (const shortcut of SHORTCUT_DEFS) {
		if (shortcut.run && !shortcut.combo.mod && !shortcut.combo.ctrl) {
			throw new Error(
				`ショートカット "${shortcut.id}" は run を持つが mod/ctrl 修飾がないため実行されません。`,
			);
		}
	}
}
